import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiArrowRight, FiCamera, FiPlayCircle } from 'react-icons/fi';
import { communityApi, motionReviewApi } from './api';
import ReviewScreen from './ReviewScreen';
import MotionReviewScreen from './MotionReviewScreen';
import { getMotionReviewOutbox } from './motionReviewOutbox';

// Keep the second pipeline available for a future release without exposing it
// or fetching its queue while community review is motion-only.
export default function CommunityWorkspace({ yoloApi = communityApi, motionApi = motionReviewApi, demo = false, onBusyChange, yoloEnabled = process.env.REACT_APP_COMMUNITY_YOLO_ENABLED === 'true' }) {
  const [selectedPipeline, setPipeline] = useState(() => yoloEnabled ? 'home' : 'motion');
  const pipeline = yoloEnabled ? selectedPipeline : 'motion';
  const [counts, setCounts] = useState({ yolo: null, motion: null });
  const [motionBusy, setMotionBusy] = useState(false);
  const scope = typeof motionApi.scope === 'function' ? motionApi.scope() : motionApi.scope;
  const outbox = useMemo(() => motionApi.buffer && scope ? getMotionReviewOutbox({ scope, api: motionApi, isActive: () => !motionApi.isActive || motionApi.isActive(scope) }) : null, [motionApi, scope]);
  useEffect(() => {
    if (!outbox) return undefined;
    const unsubscribe = outbox.subscribe((state) => {
      if (typeof state.reviewedCount === 'number') setCounts((previous) => ({ ...previous, motion: Math.max(0, state.reviewedCount + (state.pendingReviewedCount ?? state.pendingCount) + (state.countDelta || 0)) }));
    });
    Promise.resolve(outbox.refresh()).catch(() => { /* The review screen shows any durable-storage failure. */ });
    return unsubscribe;
  }, [outbox]);
  useEffect(() => {
    if (pipeline !== 'home') return undefined;
    let active = true;
    // A single read per pipeline supplies persisted progress; queue pagination
    // belongs to the review screen, not the home page.
    Promise.allSettled([yoloApi.next(), motionApi.progress ? motionApi.progress() : motionApi.next()]).then(async (results) => {
      if (!active) return;
      if (outbox && results[1].status === 'fulfilled') {
        try { await outbox.adoptServer(results[1].value); }
        catch (_) { /* Storage failures are shown in motion review; YOLO remains usable. */ }
      }
      if (!active) return;
      const motionState = outbox?.getSnapshot();
      setCounts((previous) => ({
        yolo: results[0].status === 'fulfilled' ? Number(results[0].value.reviewedCount) || 0 : previous.yolo,
        motion: typeof motionState?.reviewedCount === 'number' ? Math.max(0, motionState.reviewedCount + (motionState.pendingReviewedCount ?? motionState.pendingCount) + (motionState.countDelta || 0)) : results[1].status === 'fulfilled' ? Number(results[1].value.reviewedCount) || 0 : previous.motion,
      }));
    });
    return () => { active = false; };
  }, [motionApi, outbox, pipeline, yoloApi]);
  const enter = (value) => { setMotionBusy(false); setPipeline(value); window.scrollTo?.({ top: 0, behavior: 'instant' }); };
  if (pipeline === 'home') return <main className="cr-main cr-home">
    <span className="cr-eyebrow">A few minutes can make a difference</span>
    <h1>What would you like to help with?</h1>
    <p className="cr-home-intro">Find the wildlife we might have missed, or help our cameras get animal names right. Pick a review to get started.</p>
    {demo && <p className="cr-demo-label">Design preview · Reviews stay in this browser and are not submitted.</p>}
    <div className="cr-pipeline-grid">
      <section className="cr-pipeline-card cr-pipeline-card--motion"><FiPlayCircle className="cr-pipeline-icon" aria-hidden="true" /><span className="cr-eyebrow">Watch a short clip</span><h2>Motion review</h2>
        <p>Animal or false alarm? Check short motion clips and flag predators or sightings worth a closer look.</p>
        <p className="cr-pipeline-count" aria-label="Your motion review count"><strong>{counts.motion ?? '—'}</strong> motion reviews by you</p>
        <button className="cr-primary" onClick={() => enter('motion')}>Review motion <FiArrowRight aria-hidden="true" /></button>
      </section>
      <section className="cr-pipeline-card"><FiCamera className="cr-pipeline-icon" aria-hidden="true" /><span className="cr-eyebrow">Check a screenshot</span><h2>YOLO review</h2>
        <p>Did the AI name the animal correctly? Check the animal in the box and suggest a better identification.</p>
        <p className="cr-pipeline-count" aria-label="Your YOLO review count"><strong>{counts.yolo ?? '—'}</strong> YOLO reviews by you</p>
        <button className="cr-primary" onClick={() => enter('yolo')}>Review animal IDs <FiArrowRight aria-hidden="true" /></button>
      </section>
    </div>
    <p className="cr-small cr-muted">You don’t need to be an expert. “Not sure” is a useful answer in either review.</p>
  </main>;
  return <>
    {yoloEnabled && <div className="cr-pipeline-nav">
      {yoloEnabled && <nav aria-label="Community review navigation"><button className="cr-text-button" disabled={pipeline === 'motion' && motionBusy} onClick={() => enter('home')}><FiArrowLeft aria-hidden="true" /> Back to review home</button></nav>}
      {pipeline === 'yolo' && <span aria-label="Your YOLO review count" aria-live="polite"><strong>{counts.yolo ?? '—'}</strong> YOLO reviews by you</span>}
    </div>}
    {pipeline === 'motion' ? <MotionReviewScreen api={motionApi} demo={demo} showLeaderboard reviewedCount={counts.motion} onBusyChange={(value) => { setMotionBusy(value); onBusyChange?.(value); }} onCountChange={(value) => setCounts((previous) => ({ ...previous, motion: value }))} /> : <ReviewScreen api={yoloApi} demo={demo} onCountChange={(value) => setCounts((previous) => ({ ...previous, yolo: value }))} />}
  </>;
}
